/**
 * UnifiedPlacementPalette Component
 * Command palette for adding fixtures, controls, and products to the floor plan
 * Uses unified search with relevance scoring and category/product distinction
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { sanitizeSVG } from '@/lib/sanitize';
import { unescapeSvg, resizeSvg } from '@/lib/svgNormalizer';
import { QuickPlaceGrid } from './QuickPlaceGrid';
import { useUnifiedSearch } from '@/features/search/hooks/useUnifiedSearch';
import type { SearchableItem } from '@/features/search/types';
import type { PlacementItem, RecentItem } from '../hooks/useUnifiedPlacement';
import { logger } from '@/lib/logger';

interface UnifiedPlacementPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (item: PlacementItem) => void;
  recentItems?: RecentItem[];
  filter?: 'all' | 'fixture' | 'control' | 'product' | null;
  onCreateWithAI?: (query: string) => void;
}

export function UnifiedPlacementPalette({
  open,
  onOpenChange,
  onSelect,
  recentItems = [],
  filter = 'all',
  onCreateWithAI,
}: UnifiedPlacementPaletteProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllProducts, setShowAllProducts] = useState(false);

  // Reset search and expansion state when closing
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setShowAllCategories(false);
      setShowAllProducts(false);
    }
  }, [open]);

  // Build recent IDs for score boosting
  const recentIds = useMemo(() => {
    return recentItems.map(r => {
      // Build ID that matches SearchableItem format
      if (r.productLibraryId) {
        return `product:${r.productLibraryId}`;
      }
      // For categories: category:domain:category:subcategory
      const domain =
        r.type === 'fixture'
          ? 'lighting_fixtures'
          : r.type === 'control'
            ? 'lighting_controls'
            : 'lv_products';
      return `category:${domain}:${r.category}:${r.subcategory || ''}`;
    });
  }, [recentItems]);

  // Use unified search with scoring
  const {
    categories: searchCategories,
    products: searchProducts,
    all: allResults,
    isLoading: isSearching,
    error: searchError,
  } = useUnifiedSearch(searchQuery, {
    recentIds,
    maxCategories: showAllCategories ? 50 : 15,
    maxProducts: showAllProducts ? 30 : 10,
  });

  // Debug logging for unified search
  useEffect(() => {
    if (searchQuery && searchQuery.length >= 2) {
      logger.log('[Palette] Unified search:', {
        query: searchQuery,
        isSearching,
        categoryCount: searchCategories.length,
        productCount: searchProducts.length,
        totalResults: allResults.length,
        error: searchError?.message,
      });
    }
  }, [
    searchQuery,
    isSearching,
    searchCategories.length,
    searchProducts.length,
    allResults.length,
    searchError,
  ]);

  // Helper to render category icon
  const renderCategoryIcon = useCallback(
    (iconSvg?: string, iconUrl?: string, color?: string, size = 16) => {
      if (iconSvg) {
        // Clean and resize the SVG - normalization happens at upload time
        const cleanedSvg = unescapeSvg(iconSvg);
        const processedSvg = resizeSvg(cleanedSvg, size, size);
        const targetColor = color || '#6B7280';

        return (
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: size, height: size, color: targetColor }} // CSS currentColor inheritance
            dangerouslySetInnerHTML={{ __html: sanitizeSVG(processedSvg) }}
          />
        );
      }
      if (iconUrl) {
        return (
          <img
            src={iconUrl}
            alt=""
            className="flex-shrink-0 object-contain"
            style={{ width: size, height: size }}
          />
        );
      }
      return null;
    },
    [],
  );

  // Handle SearchableItem selection (unified handler)
  const handleSearchableItemSelect = useCallback(
    (item: SearchableItem) => {
      const placementItem: PlacementItem = {
        type: item.placementType,
        category: item.category,
        subcategory: item.subcategory,
        productLibraryId: item.productLibraryId,
        displayName: item.displayName,
        iconSvg: item.icon.svg,
        iconUrl: item.icon.url,
        iconColor: item.icon.color,
      };
      onSelect(placementItem);
      logger.log('[Palette] Item selected:', {
        kind: item.kind,
        displayName: item.displayName,
        score: item.matchScore,
        isRecent: item.isRecent,
      });
    },
    [onSelect],
  );

  // Handle recent item selection
  const handleRecentSelect = useCallback(
    (recent: RecentItem) => {
      const item: PlacementItem = {
        type: recent.type,
        category: recent.category,
        subcategory: recent.subcategory,
        productLibraryId: recent.productLibraryId,
        displayName: recent.displayName,
        iconSvg: recent.iconSvg,
        iconUrl: recent.iconUrl,
        iconColor: recent.iconColor,
      };
      onSelect(item);
      logger.log('[Palette] Recent item selected:', item.displayName);
    },
    [onSelect],
  );

  // Filter results based on type filter
  const filteredCategories = useMemo(() => {
    if (filter === 'all') return searchCategories;
    return searchCategories.filter(item => item.placementType === filter);
  }, [searchCategories, filter]);

  const filteredProducts = useMemo(() => {
    if (filter === 'all' || filter === 'product') return searchProducts;
    return [];
  }, [searchProducts, filter]);

  // Helper to render SearchableItem icon
  const renderSearchableIcon = useCallback(
    (item: SearchableItem, size = 20) => {
      const { icon } = item;
      if (icon.type === 'svg' && icon.svg) {
        return renderCategoryIcon(icon.svg, undefined, icon.color, size);
      }
      if (icon.type === 'url' && icon.url) {
        return (
          <img
            src={icon.url}
            alt=""
            className="flex-shrink-0 object-contain rounded bg-gray-100"
            style={{ width: size, height: size }}
          />
        );
      }
      // Abbreviation fallback
      return (
        <div
          className="flex-shrink-0 rounded flex items-center justify-center text-[10px] font-medium text-white"
          style={{
            width: size,
            height: size,
            backgroundColor: icon.color || '#6B7280',
          }}
        >
          {icon.abbreviation || item.displayName.charAt(0).toUpperCase()}
        </div>
      );
    },
    [renderCategoryIcon],
  );

  // Check if we're in search mode (at least 2 characters typed)
  const isSearchMode = searchQuery.length >= 2;
  const hasResults = filteredCategories.length > 0 || filteredProducts.length > 0;

  // Categories already in Quick Place (to exclude from Most Used)
  // Note: These are normalized to lowercase for comparison
  const quickPlaceCategories = useMemo(
    () =>
      new Set([
        'fixture:recessed',
        'control:control:keypad',
        'product:speaker',
        'product:camera',
        'product:access_point',
        'product:display:tv',
      ]),
    [],
  );

  // Derive most-used categories from recent items
  // Groups by category and counts total usage, excluding Quick Place items
  const mostUsedCategories = useMemo(() => {
    const categoryUsage = new Map<string, { item: RecentItem; count: number }>();

    recentItems.forEach(item => {
      // Build a unique key for this category (normalized to lowercase for comparison)
      const key = `${item.type}:${item.category.toLowerCase()}${item.subcategory ? ':' + item.subcategory.toLowerCase() : ''}`;

      // Skip if this is already in Quick Place
      if (quickPlaceCategories.has(key)) return;

      const existing = categoryUsage.get(key);
      if (existing) {
        existing.count += item.useCount;
      } else {
        categoryUsage.set(key, { item, count: item.useCount });
      }
    });

    // Sort by count and take top 6
    return Array.from(categoryUsage.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [recentItems, quickPlaceCategories]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <CommandInput
        placeholder="Search fixtures, controls, products..."
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>
          {isSearching
            ? 'Searching...'
            : searchQuery.length === 1
              ? 'Type one more character to search...'
              : 'No results found.'}
        </CommandEmpty>

        {/* Quick Place Grid - only show when no search */}
        {!searchQuery && (
          <>
            <CommandGroup heading="Quick Place">
              <div className="px-2 py-2">
                <QuickPlaceGrid onSelect={onSelect} showMore={false} />
              </div>
              {/* New Product (AI) button */}
              {onCreateWithAI && (
                <CommandItem
                  value="new-product-ai"
                  onSelect={() => {
                    onCreateWithAI('');
                    onOpenChange(false);
                  }}
                  className="flex items-center gap-2 mx-2 mb-2 py-2 data-[selected=true]:bg-gray-100"
                >
                  <Sparkles className="w-3 h-3 text-muted-foreground" />
                  <span>New Product (AI)</span>
                </CommandItem>
              )}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Most Used Categories - only show when no search and has data */}
        {!searchQuery && mostUsedCategories.length > 0 && (
          <>
            <CommandGroup heading="Most Used">
              {mostUsedCategories.map(({ item }, index) => (
                <CommandItem
                  key={`most-used-${index}-${item.category}`}
                  value={`most-used-${index}`}
                  onSelect={() => handleRecentSelect(item)}
                  className="flex items-center gap-3 py-2 data-[selected=true]:bg-gray-100"
                >
                  {renderCategoryIcon(item.iconSvg, item.iconUrl, item.iconColor)}
                  <span className="flex-1 truncate">{item.displayName}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Categories Section - when searching */}
        {isSearchMode && filteredCategories.length > 0 && (
          <>
            <CommandGroup heading="Categories">
              {filteredCategories.map(item => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  onSelect={() => handleSearchableItemSelect(item)}
                  className="flex items-center gap-3 py-2 data-[selected=true]:bg-gray-100"
                >
                  {/* Icon */}
                  {renderSearchableIcon(item, 20)}
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{item.displayName}</div>
                    {item.subcategory && (
                      <div className="text-xs text-muted-foreground truncate">{item.category}</div>
                    )}
                  </div>
                  {/* Badge */}
                  <span
                    className={cn(
                      'text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0',
                      item.placementType === 'fixture' && 'bg-amber-100 text-amber-700',
                      item.placementType === 'control' && 'bg-blue-100 text-blue-700',
                      item.placementType === 'product' && 'bg-purple-100 text-purple-700',
                    )}
                  >
                    {item.placementType === 'fixture'
                      ? 'Fixture'
                      : item.placementType === 'control'
                        ? 'Control'
                        : 'Category'}
                  </span>
                </CommandItem>
              ))}
              {/* Show more categories */}
              {filteredCategories.length >= 15 && !showAllCategories && (
                <CommandItem
                  value="show-more-categories"
                  onSelect={() => setShowAllCategories(true)}
                  className="text-muted-foreground text-sm justify-center py-2"
                >
                  Show more categories...
                </CommandItem>
              )}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Products Section - when searching */}
        {isSearchMode && filteredProducts.length > 0 && (
          <>
            <CommandGroup heading="Products">
              {filteredProducts.map(item => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  onSelect={() => handleSearchableItemSelect(item)}
                  className="flex items-center gap-3 py-2 data-[selected=true]:bg-gray-100"
                >
                  {/* Icon/Image */}
                  {renderSearchableIcon(item, 32)}
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{item.displayName}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {item.secondaryText || `${item.brand || ''} / ${item.category}`}
                    </div>
                  </div>
                  {/* Badge */}
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-600 flex-shrink-0">
                    Product
                  </span>
                </CommandItem>
              ))}
              {/* Show more products */}
              {filteredProducts.length >= 10 && !showAllProducts && (
                <CommandItem
                  value="show-more-products"
                  onSelect={() => setShowAllProducts(true)}
                  className="text-muted-foreground text-sm justify-center py-2"
                >
                  Show more products...
                </CommandItem>
              )}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Recent Items Section - only show when no search */}
        {!searchQuery && recentItems.length > 0 && (
          <CommandGroup heading="Recent">
            {recentItems.slice(0, 5).map((recent, index) => (
              <CommandItem
                key={`recent-${index}-${recent.category}`}
                value={`recent-${index}`}
                onSelect={() => handleRecentSelect(recent)}
                className="flex items-center gap-3 py-2 data-[selected=true]:bg-gray-100"
              >
                {renderCategoryIcon(recent.iconSvg, recent.iconUrl, recent.iconColor)}
                <span className="flex-1 truncate">{recent.displayName}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* AI Create Option - when searching with no results */}
        {isSearchMode && !hasResults && !isSearching && onCreateWithAI && (
          <CommandGroup heading="Create">
            <CommandItem
              value="create-ai"
              className="flex items-center gap-2 py-2 data-[selected=true]:bg-gray-100"
              onSelect={() => {
                onCreateWithAI(searchQuery);
                onOpenChange(false);
              }}
            >
              <Sparkles className="w-3 h-3 text-muted-foreground" />
              <span>Create "{searchQuery}" with AI...</span>
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}

export default UnifiedPlacementPalette;
